import React, { useEffect, useState, useRef, type ChangeEvent, type MouseEvent } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';
import Search from '@jwp/ott-theme/assets/icons/search.svg?react';
import env from '@jwp/ott-common/src/env';
import useDebounce from '@jwp/ott-hooks-react/src/useDebounce';
import { fetchLibraryList, signupUser, type UserData, type LibraryBranch, type LibraryItem, type Restriction } from '@jwp/ott-common/src/controllers/graphql';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { modalURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { COOKIE_DOMAIN, getLocalStorageValue, SOCCIAL_LOGIN_OPENATHENS } from '@jwp/ott-common/src/constants';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import Cookies from 'js-cookie';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { getModule } from '@jwp/ott-common/src/modules/container';
import FormFeedback from '../FormFeedback/FormFeedback';

import { getSiteType } from '../../utils/useSiteType';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import GoogleMap from '../librarymapview/MapView';
// import MapIcon from '../../../../theme/assets/icons/map_icon.svg';

import styles from './Autocomplete.module.scss';

const PENDING_LIBRARY_SELECTION_KEY = 'pendingLibrarySelection';


export type LibrarySelectionPayload = {
  libraryId: string;
  libraryName: string;
  libraryParent: Restriction[];
  withInOrganization: boolean;
  libraryType: string;
};

type Props = {
  onLibrarySelected?: (payload: LibrarySelectionPayload) => void;
  registrationStepLabel?: string;
};

const LibraryAutocomplete = ({ onLibrarySelected, registrationStepLabel }: Props) => {
  const accountController = getModule(AccountController);
  const { t, i18n } = useTranslation('account');
  const navigate = useNavigate();
  const location = useLocation();
  const [libraryName, setLibraryName] = useState('');
  const [libraryListData, setLibraryListData] = useState<LibraryItem[]>([]);
  const [signupRouteError, setSignupRouteError] = useState((location.state as { signupErrorMessage?: string } | null | undefined)?.signupErrorMessage ?? undefined);
  const pageNumber = useRef(1);
  const loadedPage = useRef(0);
  const [selectedLibrary, setSelectedLibrary] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const [siteType, setSiteType] = useState<string>('');
  const [isScrollLoading, setIsScrollLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const locationRef = useRef({});
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint < Breakpoint.lg;

  const config = useConfigStore((s) => s.config);
  const LocalStorageValue = getLocalStorageValue();
  const defaultLanguage = config?.custom?.defaultLanguage;
  const htmlLang = i18n.language !== env.APP_DEFAULT_LANGUAGE ? env.APP_DEFAULT_LANGUAGE : undefined;

  useEffect(() => {
    return () => {
      sessionStorage.removeItem('isSignInProgress');
    };
  }, []);

  const debouncedFetchLibraryList = useDebounce(() => {
    refetchLibraryList();
  }, 300);

  const mutation = useMutation(signupUser, {
    onMutate: () => {
      setSignupRouteError(undefined);
    },
    onError: (error: Error) => {
      console.error('Mutation error:', error);
    },
    onSuccess: (data) => {
      if (data) {
        Cookies.set('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }), {
          domain: COOKIE_DOMAIN,
          expires: 7,
        });
        localStorage.setItem('SelectedLibraryData', JSON.stringify({ SelectedLibraryData: data }));
        sessionStorage.removeItem('isSignInProgress');
        accountController.getAccount();
        navigate(modalURLFromLocation(location, location?.state?.isComeFrom === 'login' && location?.state?.isComeFromEditPage ? null : 'welcome'));
      }
    },
  });

  const { isLoading: signupUserLoading, isError, error } = mutation;

  // reset LibraryListData and pageNumber on libraryName change
  useEffect(() => {
    debouncedFetchLibraryList();
    pageNumber.current = 1;
    loadedPage.current = 0;
    setLibraryListData([]);
    setHasMore(true);
  }, [libraryName, debouncedFetchLibraryList]);

  useEffect(() => {
    const siteType = getSiteType();
    setSiteType(siteType);
  }, []);

  const { refetch: refetchLibraryList, isLoading } = useQuery(
    ['libraryList', libraryName, siteType],
    async () => {
      // keep the requested page with the response, the page is not part of the query key
      const page = pageNumber.current;
      const list: LibraryItem[] = await fetchLibraryList({
        libraryName,
        page,
        perPage: 10,
        isGetAllLibrary: siteType === 'public' ? false : true,
        libraryType: siteType,
      });

      return { page, list };
    },
    {
      enabled: !!siteType,
      refetchOnWindowFocus: false,
      onSuccess: ({ page, list: data }) => {
        // response of another page (the refetch joined a request that was already in flight), fetch the wanted page
        if (page !== pageNumber.current) {
          refetchLibraryList();
          return;
        }

        // first page replaces the list, the next page is appended, a page that is already in the list is ignored
        if (page === 1) {
          setLibraryListData([...data]);
        } else if (page === loadedPage.current + 1) {
          setLibraryListData((prevList) => [...prevList, ...data]);
        }
        loadedPage.current = page;
        setIsScrollLoading(false);
        // Check if we have more data
        if (data.length < 10) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      },
    },
  );

  useEffect(() => {
    if (siteType) {
      refetchLibraryList();
    }
  }, [siteType, refetchLibraryList]);

  useEffect(() => {
    const container = document.getElementById('scroll-container');

    const handleScroll = () => {
      if (!isScrollLoading && hasMore && container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        if (scrollTop + clientHeight + 50 >= scrollHeight) {
          // based on the loaded page, so repeated scroll events can't skip a page
          pageNumber.current = loadedPage.current + 1;
          refetchLibraryList();
          setIsScrollLoading(true);
        }
      }
    };

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [isScrollLoading, refetchLibraryList, hasMore, siteType]);
  // using eslint disable line for prevent a empty dependency array.

  const handleLibraryChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLibraryName(e.target.value);
  };

  const handleLibrarySelect = (e: MouseEvent<HTMLLIElement>, libraryId: string, withInOrganization: boolean, libraryLon: number, libraryLat: number) => {
    e.stopPropagation();

    if (!withInOrganization) {
      setNotification(
        location?.state?.isComeFromEditPage
          ? 'The selected library is not part of our system. Please select a different one.'
          : t('Library_Access.Library_auto_complete.Notification_message'),
      );
    }

    setSelectedLibrary(libraryId);
    locationRef.current = { libraryLon: libraryLon, libraryLat: libraryLat };
  };

  const routeLibrarySelect = (libraryId: string, libraryName: string, libraryParent: Restriction[], withInOrganization: boolean, libraryType: string,entityId: string) => {
    const selectedLibraryPayload: LibrarySelectionPayload = {
      libraryId,
      libraryName,
      libraryParent,
      withInOrganization,
      libraryType,
    };

    if (siteType === 'public') {
      if (onLibrarySelected) {
        onLibrarySelected(selectedLibraryPayload);
        return;
      }

      navigate(modalURLFromLocation(location, 'library-access'), {
        state: { libraryId, libraryName, libraryParent, withInOrganization, libraryType },
      });
    } else if (siteType === 'academic') {
      if(entityId) {
        window.location.href = SOCCIAL_LOGIN_OPENATHENS.replace('{openathensEntityId}', entityId);
        return;
      }
      let token = '';
      try {
        const storedToken = localStorage.getItem('inplayer_token');
        if (storedToken) {
          token = JSON.parse(storedToken).token;
        }
      } catch (error) {
        console.error('Error parsing token from localStorage:', error);
      }
      const userData = {
        userToken: token,
        libraryId: libraryId,
        isAdult: true,
        skipped: true,
        languageCode: LocalStorageValue ?? defaultLanguage,
      };

      if (!token) {
        sessionStorage.setItem(PENDING_LIBRARY_SELECTION_KEY, JSON.stringify(selectedLibraryPayload));
        if(onLibrarySelected) {
          onLibrarySelected(selectedLibraryPayload);
          return;
        }
      }

      mutation.mutate(userData as UserData);
    }
  };

  const navigateMap = () => {
    navigate(modalURLFromLocation(location, 'Map-view'), {
      state: { locationRef: locationRef, libraryListData: libraryListData },
    });
  };

  useEffect(() => {
    if (libraryListData && locationRef) {
      const locationState = {
        locationRef: locationRef,
        libraryListData: libraryListData,
      };
      sessionStorage.setItem('userLocation', JSON.stringify(locationState));
    }
  }, [libraryListData, locationRef]);

  return (
    <section>
      <section className={siteType === 'academic' ? styles.gridcontainerTwo : styles.gridcontainer}>
        <section>
          {notification && (
            <div className={styles.notification}>
              {notification}
              <Button
                color="primary"
                variant="contained"
                size="large"
                onClick={() => {
                  setNotification(null);
                }}
                label="OK"
                className={styles.confirmbtn}
              />
            </div>
          )}
          <div>
            {signupRouteError ? (
              <FormFeedback variant="error" visible>
                {signupRouteError}
              </FormFeedback>
            ) : null}
            {error && error.message ? (
              <FormFeedback variant="error" visible={isError}>
                {error.message}
              </FormFeedback>
            ) : null}
          </div>
          <section className={styles.main_section}>
            {registrationStepLabel ? <p className={styles.stepProgress}>{registrationStepLabel}</p> : null}
            <section>
              <h2 className={styles.libraryTitle}>
                {siteType === 'public'
                  ? t('Library_Access.Library_auto_complete.header_text_public')
                  : t('Library_Access.Library_auto_complete.header_text_academic')}
              </h2>
              <div className={styles['input-with-icon']}>
                <input
                  className={styles['library-input']}
                  id="searchbar-input"
                  type="text"
                  lang={htmlLang}
                  autoComplete="off"
                  value={libraryName}
                  onChange={handleLibraryChange}
                  placeholder={t('Library_Access.Library_auto_complete.Search_library')}
                />
                <Icon icon={Search} className={styles.icon} />
              </div>
            </section>
            {isMobile && siteType === 'public' && (
              <section className={styles.mobile_map_view} onClick={navigateMap}>
                <Icon icon={Search} />
                <h5>See map view</h5>
              </section>
            )}
            <h2 className={styles['suggested-data']}>{t('Library_Access.Library_auto_complete.Suggested_Libraries')}</h2>
          </section>
          <div className={styles['filter-options']} id="scroll-container">
            {(libraryListData?.length || 0) > 0 ? (
              libraryListData?.map((libraryItem: LibraryItem, i: number) => (
                <div key={i} className={styles['filter-data']}>
                  <div className={styles.libarayHaeding}>
                    <h2 className={styles.parentName}>{libraryItem?.libraryParent?.libraryName?.toLowerCase()}</h2>
                    <div className={styles.libaraySelection} />
                  </div>
                  <ul className={styles['library-branch-list']}>
                    {libraryItem?.libraryBranch.map(({ libraryId, libraryName, libraryLat, libraryLon, libraryType }: LibraryBranch, i: number) => (
                      <li
                        key={i}
                        className={selectedLibrary === libraryId ? styles['branch-list'] : styles['branch-list-name']}
                        onClick={(e) => handleLibrarySelect(e, libraryId, libraryItem.withInOrganization, libraryLon, libraryLat)}
                      >
                        <h4>{libraryName.toLowerCase()}</h4>
                        {selectedLibrary === libraryId && (
                          <h4
                            className={styles['hover-text']}
                            onClick={(e) => {
                              e.stopPropagation();
                              routeLibrarySelect(libraryId, libraryName, libraryItem?.restriction, libraryItem.withInOrganization, libraryType,libraryItem.libraryParent.openathensEntityId);
                            }}
                          >
                            {t('Library_Access.Library_auto_complete.Select_Library')}
                          </h4>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <div className={styles['loading-page']}>
                {isLoading ? <LoadingOverlay inline /> : <p className={styles['no-dataFound']}>{t('Library_Access.Library_auto_complete.No_data_found')}</p>}
              </div>
            )}
            {isScrollLoading && (
              <div className={styles.loader_container}>
                <div className={styles.loader}></div>
              </div>
            )}
            {signupUserLoading && <LoadingOverlay transparentBackground inline />}
          </div>
        </section>
        {!isMobile && siteType === 'public' && (
          <section>
            <GoogleMap
              libraryLists={libraryListData}
              seleteLocation={locationRef}
              selectLibraryButtonLabel={t('Library_Access.Library_auto_complete.map_select_library')}
              onLibraryBranchSelect={(branch, libraryItem) =>
                routeLibrarySelect(
                  branch.libraryId,
                  branch.libraryName,
                  libraryItem.restriction,
                  libraryItem.withInOrganization,
                  branch.libraryType,
                  libraryItem.libraryParent?.openathensEntityId ?? '',
                )
              }
            />
          </section>
        )}
      </section>
    </section>
  );
};

export default LibraryAutocomplete;
